import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useFirstVisit from '../hooks/useFirstVisit';
import './DashboardTutorial.css';

const SCHRITTE = [
  {
    targetId: 'tutorial-bento',
    pad: 16,
    radius: 18,
    titel: 'Dein Bewerbungs-Cockpit',
    inhalt: (
      <p>
        Hier hast du alles auf einen Blick – wie viele Bewerbungen du diese
        Woche verschickt hast und wie sich deine Bewerbungsrate entwickelt.
      </p>
    ),
    tooltipPos: 'bottom',
  },
  {
    targetId: 'tutorial-neu-btn',
    pad: 20,
    radius: 999,
    titel: 'Hier beginnt jede Bewerbung',
    inhalt: (
      <p>
        Füge eine Stellenanzeige per Link oder Text ein – CVFlow erstellt
        automatisch ein maßgeschneidertes Anschreiben und einen optimierten
        Lebenslauf.
      </p>
    ),
    tooltipPos: 'top',
  },
  {
    targetId: 'tutorial-panel-actions',
    pad: 10,
    radius: 14,
    titel: 'Deine Schnellzugriffe',
    inhalt: (
      <ul className="dt-list">
        <li><strong>💬 Feedback</strong> – Teile uns Tipps, Wünsche oder Bugs mit. Wir lesen alles und arbeiten aktiv daran, CVFlow für dich besser zu machen.</li>
        <li><strong>🌙 Nachtmodus</strong> – Wechsle jederzeit zwischen Hell- und Dunkelmodus.</li>
        <li><strong>👤 Profile</strong> – Leg mehrere Profile an – für verschiedene Stellen oder für jemand anderen. So hast du maximale Kontrolle über die Generierung.</li>
        <li><strong>⚙️ Einstellungen</strong> – Konto, Abrechnung und alle weiteren Optionen.</li>
      </ul>
    ),
    tooltipPos: 'bottom-left',
  },
];

function useRect(targetId) {
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  useEffect(() => {
    function tick() {
      const el = document.getElementById(targetId);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect(prev => {
          if (!prev || prev.top !== r.top || prev.left !== r.left ||
              prev.width !== r.width || prev.height !== r.height) {
            return { top: r.top, left: r.left, width: r.width, height: r.height,
                     bottom: r.bottom, right: r.right };
          }
          return prev;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [targetId]);

  return rect;
}

function TutorialOverlay({ schritt, onWeiter, onUeberspringen, gesamt }) {
  const { targetId, pad, radius, titel, inhalt, tooltipPos } = SCHRITTE[schritt];
  const rect = useRect(targetId);

  if (!rect) return null;

  const top    = rect.top    - pad;
  const left   = rect.left   - pad;
  const width  = rect.width  + pad * 2;
  const height = rect.height + pad * 2;

  // Tooltip position — for 'top', fall back to 'bottom' when button is in upper half of screen
  const TOOLTIP_W = 340;
  const effectivePos = tooltipPos === 'top' && rect.top < window.innerHeight / 2 ? 'bottom' : tooltipPos;
  let ttStyle = {};
  if (effectivePos === 'bottom') {
    ttStyle = {
      top:  rect.bottom + pad + 16,
      left: rect.left + rect.width / 2 - TOOLTIP_W / 2,
    };
  } else if (effectivePos === 'top') {
    ttStyle = {
      bottom: window.innerHeight - rect.top + pad + 16,
      left:   rect.left + rect.width / 2 - TOOLTIP_W / 2,
    };
  } else if (effectivePos === 'bottom-left') {
    ttStyle = {
      top:   rect.bottom + pad + 16,
      right: window.innerWidth - rect.right - pad,
    };
  }
  // Clamp to viewport
  if (ttStyle.left !== undefined) {
    ttStyle.left = Math.max(16, Math.min(ttStyle.left, window.innerWidth - TOOLTIP_W - 16));
  }

  return createPortal(
    <div className="dt-root" onClick={e => e.stopPropagation()}>
      {/* Dark layer */}
      <div className="dt-backdrop" />

      {/* Spotlight — big box-shadow creates the dark border around the hole */}
      <div
        className="dt-spotlight"
        style={{ top, left, width, height, borderRadius: radius }}
      />

      {/* Tooltip */}
      <div className="dt-tooltip" style={{ ...ttStyle, width: TOOLTIP_W }}>
        <div className="dt-tooltip-step">{schritt + 1} / {gesamt}</div>
        <h3 className="dt-tooltip-titel">{titel}</h3>
        <div className="dt-tooltip-inhalt">{inhalt}</div>
        <div className="dt-tooltip-actions">
          <button className="dt-skip-inline" onClick={onUeberspringen}>
            Überspringen
          </button>
          <button className="dt-weiter" onClick={onWeiter}>
            {schritt === gesamt - 1 ? 'Fertig ✓' : 'Weiter →'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function DashboardTutorial() {
  const { isFirst, dismiss } = useFirstVisit('dashboard');
  const [schritt, setSchritt] = useState(0);

  if (!isFirst) return null;

  function weiter() {
    if (schritt < SCHRITTE.length - 1) setSchritt(s => s + 1);
    else dismiss();
  }

  return (
    <TutorialOverlay
      schritt={schritt}
      onWeiter={weiter}
      onUeberspringen={dismiss}
      gesamt={SCHRITTE.length}
    />
  );
}
